// 레이드 보스 관리 시스템 - TypeScript 타입 정의
import type { 
  SubjectType, 
  ConditionTarget, 
  ConditionEvent, 
  Operator, 
  LocationCondition, 
  TimingType, 
  ActionType 
} from './improved-trigger';

// ============================================
// 메인 테이블 타입들
// ============================================

// 엔티티 타입 열거형
export enum EntityType {
  BOSS = 'BOSS',
  BOSS_DOUBLE = 'BOSS_DOUBLE',
  BOSS_PHASE = 'BOSS_PHASE',
  SUMMON = 'SUMMON',
  DUMMY = 'DUMMY',
  OBJECT = 'OBJECT',
  TRAP = 'TRAP',
  NPC = 'NPC'
}

// 새로운 통합 엔티티 타입
export interface RaidEntity {
  combat_rules?: import('../lib/combatRules').CombatRulePack | null;
  id: number;
  entity_code: string;
  entity_name: string;
  entity_type: string; // DB에서는 문자열로 저장됨
  parent_code?: string;
  description?: string;
  max_hp: number;
  phase_count: number;
  difficulty: string;
  size_tiles?: number;
  spawn_limit?: number;
  lifetime_s?: number;
  spawn_raid_code?: string;
  spawn_order?: number;
  spawn_room_code?: string;
  game_version: string;
  created_at: string;
  updated_at: string;
}

// 엔티티 속성 타입
export interface RaidEntityProperty {
  id: number;
  entity_code: string;
  property_key: string;
  property_value: string;
  property_type: 'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON';
}

// 기존 호환성을 위한 BossInfo (뷰로 제공)
export interface BossInfo {
  id: number;
  boss_code: string;
  boss_name: string;
  description?: string;
  max_hp: number;
  phase_count: number;
  difficulty: string;
  game_version: string;
  created_at: string;
  updated_at: string;
}

export interface BossSkill {
  initial_cooldown_s?: number;
  channeling_time_s?: number;
  is_interruptable?: boolean;
  id: number;
  key: string;
  name: string;
  description?: string;
  comment?: string;
  boss_code?: string; // null이면 공통 스킬
  cat: string; // skill_type -> cat
  order: number; // skill_order -> order
  required_phase: number;
  
  // 시전 정보
  casting_time_s: number;
  channel_time_s: number;
  self_cooldown_s: number;
  global_cooldown_s: number;
  init_cooldown_s: number;
  
  // 사거리/비용
  range: number;
  weight_mp_cost: number;
  
  // 차단 정보
  interruptible: string;
  is_no_silence: boolean;
  
  // 경고 시스템 (복수 선택)
  warning_ground: boolean;
  warning_cast_bar: boolean;
  warning_text: boolean;
  warning_sound: boolean;
  warning_screen: boolean;
  warning_detail?: string; // JSON 형식
  
  // 대상 정보
  target: string; // target_type -> target
  area: string; // area_type -> area
  area_radius: number;
  target_count: number;
  
  game_version: string;
  created_at: string;
  updated_at: string;
}

export interface BossEffect {
  id: number;
  key: string; // effect_key -> key
  name: string; // effect_name -> name
  description?: string;
  comment?: string;
  order: number; // effect_order -> order
  boss_code?: string; // null이면 공통 효과
  
  // 효과 분류
  cat: string; // effect_category -> cat
  effect_type?: string; // 세부 효과 타입 (inf_effect_type_renewed 참조)
  affect: string; // effect_affect -> affect
  dispel?: string; // effect_dispel -> dispel
  
  // 공유/상호작용
  is_shared: boolean;
  is_mutual: boolean;
  
  // 지속/틱 정보
  tick_period_s?: number;
  max_count: number; // max_stack -> max_count
  duration_type?: string;
  duration_value?: number;
  
  // 스탯 계산
  is_from_affected: boolean;
  operator?: string; // calculation_method -> operator
  in_stat?: string;
  out_stat?: string;
  
  // 속성
  property_category: string;
  property_element: string;
  
  game_version: string;
  created_at: string;
  updated_at: string;
}

export interface BossSkillEffectRelation {
  id?: number; // Legacy only; persisted identity is (skill, effect, order).
  skill: string; // skill key reference
  effect: string; // effect key reference
  order: number; // 필수 필드
  property?: string; // deprecated - use property_category and property_element
  property_category?: string;
  property_element?: string;
  power: number;
  duration_s?: number;
  add_count?: number;
  is_no_crit?: boolean;
  is_no_dodge?: boolean;
  is_no_block?: boolean;
  is_no_resist?: boolean;
  target_type?: string;
  target_effect?: string;
  priority: number;
  created_at?: string;
}

// V2 트리거 시스템 (현재 사용 중)
export interface TriggerDefinitionV2 {
  id?: string; // UUID
  trigger_id: string;
  trigger_name: string;
  boss_code: string;
  
  // 조건 부분
  subject?: string;
  condition_target?: string;
  condition_event?: string;
  operator?: string;
  condition_value?: string;
  location_condition?: string;
  timing?: string;
  timing_value?: number;
  
  // 체크 설정
  check_interval?: number;
  is_repeatable?: boolean;
  max_trigger_count?: number;
  priority?: number;
  
  // 추가 정보
  description?: string;
  additional_conditions?: any; // JSONB
  created_at?: string;
  updated_at?: string;
}

export interface TriggerActionRelationV2 {
  id?: string; // UUID
  trigger_id: string;
  action_order: number;
  action_type?: string;
  action_target?: string;
  action_params?: any; // JSONB
  delay?: number;
  created_at?: string;
}

// ============================================
// V2 트리거 시스템 - improved-trigger.ts에서 재내보내기
// ============================================

export * from './improved-trigger';

// V2 트리거 액션 파라미터 타입
export interface TriggerActionParams {
  skill?: string;
  effect?: string;
  dialogue?: string;
  power?: number;
  target?: string;
  phase?: number;
  entity?: string;
  duration_s?: number;
  add_count?: number;
  property_category?: string;
  property_element?: string;
  [key: string]: unknown;
}

// ============================================
// 참조 테이블 타입들
// ============================================

export interface InferenceTableBase {
  sort_order: number;
  description?: string;
}

export interface InfSkillType extends InferenceTableBase {
  skill_type_id: number;
  skill_code: string;
  skill_name: string;
}

export interface InfTargetType extends InferenceTableBase {
  target_id: number;
  target_code: string;
  target_name: string;
}

export interface InfSkillArea extends InferenceTableBase {
  area_id: number;
  area_code: string;
  area_name: string;
}

export interface InfInterruptible extends InferenceTableBase {
  interrupt_id: number;
  interrupt_code: string;
  interrupt_name: string;
}

export interface InfEffectType extends InferenceTableBase {
  effect_id: number;
  effect_code: string;
  effect_name: string;
  value?: number;
}

export interface InfEffectAffect extends InferenceTableBase {
  affect_id: number;
  affect_code: string;
  affect_name: string;
  is_self_affected?: boolean;
  is_hero_affected?: boolean;
  is_enemy_affected?: boolean;
  is_coord_affected?: boolean;
}

export interface InfEffectDispel extends InferenceTableBase {
  dispel_id: number;
  dispel_code: string;
  dispel_name: string;
}

export interface InfDurationType extends InferenceTableBase {
  duration_id: number;
  duration_code: string;
  duration_name: string;
}

export interface InfPowerType extends InferenceTableBase {
  power_id: number;
  power_code: string;
  power_name: string;
  calculation_method: string;
  requires_in_stat: boolean;
  requires_out_stat: boolean;
}

export interface InfStat extends InferenceTableBase {
  stat_id: number;
  stat_code: string;
  stat_name: string;
  stat_category: string;
  is_percentage?: boolean;
}

export interface InfCalculationMethod extends InferenceTableBase {
  method_id: number;
  method_code: string;
  method_name: string;
  formula_example: string;
}


export interface InfDifficulty extends InferenceTableBase {
  difficulty_id: number;
  difficulty_code: string;
  difficulty_name: string;
  hp_multiplier: number;
  damage_multiplier: number;
  reward_multiplier: number;
}

export interface InfMechanismType extends InferenceTableBase {
  mechanism_id: number;
  mechanism_code: string;
  mechanism_name: string;
}

// ============================================
// 폼 데이터 타입들
// ============================================

// 엔티티 폼 데이터
export interface EntityFormData {
  entity_code: string;
  entity_name: string;
  entity_type: EntityType;
  parent_code?: string;
  description?: string;
  max_hp: number;
  phase_count: number;
  difficulty: string;
  size_tiles?: number;
  spawn_limit?: number;
  lifetime_s?: number;
  game_version: string;
}

// 기존 호환성을 위한 BossFormData
export interface BossFormData {
  id?: number; // auto-generated
  boss_code: string;
  boss_name: string;
  description?: string;
  max_hp: number;
  phase_count: number;
  difficulty: string;
  game_version: string;
}

export interface SkillFormData {
  key: string;
  name: string;
  description?: string;
  comment?: string;
  boss_code?: string;
  cat: string;
  order: number;
  required_phase: number;
  casting_time_s: number;
  channel_time_s: number;
  self_cooldown_s: number;
  global_cooldown_s: number;
  init_cooldown_s: number;
  range: number;
  mp_cost?: number;
  weight_mp_cost: number;
  interruptible: string;
  is_no_silence: boolean;
  warning_ground: boolean;
  warning_cast_bar: boolean;
  warning_text: boolean;
  warning_sound: boolean;
  warning_screen: boolean;
  warning_detail?: string;
  target: string; // target_type -> target
  area: string; // area_type -> area
  area_radius: number;
  target_count: number;
  game_version?: string;
}

export interface EffectFormData {
  key: string;
  name: string;
  description?: string;
  comment?: string;
  order: number;
  boss_code?: string;
  cat: string;
  affect: string;
  dispel?: string;
  is_shared: boolean;
  is_mutual: boolean;
  tick_period_s?: number;
  max_count: number; // max_stack -> max_count
  duration_type?: string;
  duration_value?: number;
  is_from_affected: boolean;
  method?: string;
  in_stat?: string;
  out_stat?: string;
  property_category: string;
  property_element: string;
  special_params?: Record<string, unknown>;
  game_version?: string;
}

export interface TriggerFormData {
  trigger_id: string;
  trigger_name: string;
  boss_code: string;
  
  // 조건 부분
  subject?: string;
  condition_target?: string;
  condition_event?: string;
  operator?: string;
  condition_value?: string;
  location_condition?: string;
  timing?: string;
  timing_value?: number;
  
  // 체크 설정
  check_interval?: number;
  is_repeatable?: boolean;
  max_trigger_count?: number;
  priority?: number;
  
  // 추가 정보
  description?: string;
  additional_conditions?: any;
}

// ============================================
// 유틸리티 타입들
// ============================================

export interface ReferenceData {
  skillTypes: InfSkillType[];
  targetTypes: InfTargetType[];
  skillAreas: InfSkillArea[];
  interruptibles: InfInterruptible[];
  effectTypes: InfEffectType[];
  effectAffects: InfEffectAffect[];
  effectDispels: InfEffectDispel[];
  durationTypes: InfDurationType[];
  powerTypes: InfPowerType[];
  stats: InfStat[];
  calculationMethods: InfCalculationMethod[];
  difficulties: InfDifficulty[];
  mechanismTypes: InfMechanismType[];
  // V2 트리거 시스템 참조 데이터
  triggerV2?: {
    subjectTypes: SubjectType[];
    conditionTargets: ConditionTarget[];
    conditionEvents: ConditionEvent[];
    operators: Operator[];
    locationConditions: LocationCondition[];
    timingTypes: TimingType[];
    actionTypes: ActionType[];
  };
}

export interface CopyModalState {
  isOpen: boolean;
  itemType: 'skill' | 'effect' | 'trigger';
  sourceItem: BossSkill | BossEffect | TriggerDefinitionV2 | null;
  itemName: string;
}

// 경고 상세 설정 타입
export interface WarningDetail {
  text?: {
    message: string;
    duration: number;
    position: 'center' | 'top' | 'bottom' | 'boss_nameplate' | 'player_center';
    size?: 'small' | 'medium' | 'large' | 'huge';
    color?: string;
    animation?: 'none' | 'fade' | 'flash' | 'slide' | 'zoom';
    delay?: number;
  };
  ground?: {
    shape: 'circle' | 'sector' | 'rectangle' | 'line' | 'donut';
    range: number;
    angle?: number;
    inner_radius?: number;
    width?: number;
    color?: string;
    fade_in?: number;
    delay?: number;
  };
  sound?: {
    file: string;
    volume: number;
  };
  cast_bar?: {
    color: string;
    position: string;
  };
  screen?: {
    effect: 'flash' | 'shake' | 'fade';
    color?: string;
    intensity?: number;
  };
}

// API 응답 타입
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// 페이지네이션 타입
export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  meta: PaginationMeta;
}

// 대사 관련 타입 (JSON 배열로 저장되는 여러 대사)
export interface DialogueData {
  texts: string[]; // 여러 대사 텍스트
  chances?: number[]; // 각 대사의 확률 (합계 1.0)
}

// 확장된 엔티티 데이터 (관계 포함)
export interface EntityWithRelations extends RaidEntity {
  skills: BossSkill[];
  effects: BossEffect[];
  triggers: TriggerDefinitionV2[];
  properties: RaidEntityProperty[];
  parent?: RaidEntity;
  children?: RaidEntity[];
}

// 기존 호환성을 위한 BossWithRelations
export interface BossWithRelations extends BossInfo {
  skills: BossSkill[];
  effects: BossEffect[];
  triggers: TriggerDefinitionV2[];
}

// 확장된 스킬 데이터 (효과 관계 포함)
export interface SkillWithEffects extends BossSkill {
  effects: (BossSkillEffectRelation & { effect: BossEffect })[];
}

// 확장된 트리거 데이터 (액션 관계 포함)
export interface TriggerWithActions extends TriggerDefinitionV2 {
  actions: TriggerActionRelationV2[];
}

// 자동 Key 생성 옵션
export interface KeyGenerationOptions {
  type: 'skill' | 'effect' | 'trigger';
  bossCode: string;
  prefix?: string;
}

// 복사 옵션
export interface CopyOptions {
  sourceKey: string;
  targetBossCode: string;
  resourceType: 'skill' | 'effect' | 'trigger';
  copyRelations?: boolean;
  newName?: string;
}

// ============================================
// 애니메이션 관련 타입들
// ============================================

export enum MotionType {
  BASIC = 'BASIC',
  LOOP = 'LOOP',
  OTHER = 'OTHER'
}

export interface BossAnimation {
  id?: number;
  boss_code: string;
  action_type: string; // 'idle', 'death', 'hit', 'interrupted', or skill key
  motion_type: MotionType;
  montage_name: string; // Single montage name
  sound_effect?: string; // Single sound effect file name (deprecated)
  voice_file?: string; // Single voice/dubbing file name (deprecated)
  has_sound_effect?: boolean | null; // Sound effect status: null=not needed, false=needed but pending, true=completed
  has_vfx?: boolean | null; // VFX status: null=not needed, false=needed but pending, true=completed
  has_animation?: boolean; // Animation completion status (always required): false=pending, true=completed
  icon_url?: string; // URL to icon in Supabase storage
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface AnimationFormData {
  id?: number;
  boss_code: string;
  action_type: string;
  motion_type: MotionType;
  montage_name: string;
  has_sound_effect?: boolean | null; // Sound effect status: null=not needed, false=needed but pending, true=completed
  has_vfx?: boolean | null; // VFX status: null=not needed, false=needed but pending, true=completed  
  has_animation?: boolean; // Animation completion status (always required): false=pending, true=completed
  icon_url?: string;
  sort_order?: number;
}

// Group animations by action for display
export interface AnimationGroup {
  action_type: string;
  action_name: string;
  is_default: boolean;
  icon_url?: string;
  animations: BossAnimation[];
}

export default {};
